// Libs
import classNames from 'classnames/bind';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseSelect } from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IBaseOption } from '~/utils/interface/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IQueryMasterData } from '~/utils/interface/masterData';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListMasterData } from '~/thunks/masterData/masterDataThunk';
import { TypeMasterDataEnum, TypeTabCreateEstimateEnum } from '~/utils/enum/common';
import { IFormEstimate } from '~/utils/interface/estimate';
// Styles, images, icons
import styles from './CreateEstimateLeadSourceTab.module.scss';

type Props = {
  onEdit?: (data: IFormEstimate, type: string) => void;
  onCancelEdit: () => void;
};

const cx = classNames.bind(styles);

const CreateEstimateLeadSourceTab = (props: Props) => {
  //#region Destructuring Props
  const { onEdit, onCancelEdit } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  const {
    control,
    getValues,
    formState: { isDirty },
  } = useFormContext<IFormEstimate>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoadingLeadSource, setIsLoadingLeadSource] = useState(false);
  const [leadSource, setLeadSource] = useState<IBaseOption[]>([]);
  const [leadSourceSearchKey, setLeadSourceSearchKey] = useState<string>('');
  const debounceLeadSourceSearchKey = useDebounce(leadSourceSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetListLeadSource = useCallback(
    (params: IQueryMasterData) => {
      setIsLoadingLeadSource(true);

      dispatch(getListMasterData(params))
        .unwrap()
        .then((res) => {
          const { responses } = res?.data;

          if (!responses) return;

          const newData: IBaseOption[] = responses?.map((item) => ({
            value: item.id,
            label: item.name,
          }));

          setLeadSource(newData);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingLeadSource(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    const payload: IQueryMasterData = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      type: TypeMasterDataEnum.LEAD_SOURCE,
      ...(debounceLeadSourceSearchKey && { textSearch: debounceLeadSourceSearchKey }),
    };

    handleGetListLeadSource(payload);
  }, [debounceLeadSourceSearchKey, handleGetListLeadSource]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSearchChange = (searchKey: string) => {
    setLeadSourceSearchKey(searchKey);
  };

  const handleSubmitFormLeadSource = () => {
    const dataForm = getValues();
    const dataBody: IFormEstimate = {
      clientId: EMPTY_STRING,
      leadSourceId: dataForm?.leadSourceId || EMPTY_STRING,
    };

    onEdit && onEdit(dataBody, TypeTabCreateEstimateEnum.LEAD_SOURCE);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='createJobLeadSourceTabComponent' className={cx('container')}>
      <Grid2 container rowSpacing={'24px'} columnSpacing={'12px'}>
        <Grid2 size={12} container rowSpacing={'24px'} columnSpacing={'12px'}>
          <Grid2 size={12}>
            <Controller
              name='leadSourceId'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseSelect
                  label={t('estimate_lead_source_title')}
                  value={value}
                  onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                  isLoading={isLoadingLeadSource}
                  onSearchChange={handleSearchChange}
                  options={leadSource}
                />
              )}
            />
          </Grid2>
          {estimateId && (
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'flex-end'}
              component={'div'}
              spacing={'12px'}
              className={cx('formFooter')}
            >
              <Button variant='outlined' onClick={onCancelEdit}>
                {t('common_action_cancel', { ns: 'common/base' })}
              </Button>

              <Button
                variant='contained'
                type='button'
                onClick={handleSubmitFormLeadSource}
                disabled={!isDirty}
              >
                {t('common_action_save', { ns: 'common/base' })}
              </Button>
            </Stack>
          )}
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default CreateEstimateLeadSourceTab;
